/* eslint-disable react/destructuring-assignment */
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { space } from 'styled-system';
import { debounce } from 'lodash';
import { Classes, InputGroup as BlueprintInputGroup } from '@blueprintjs/core';

import { Button, Flex } from 'core/components';
import { formAriaProps } from 'core/util/ariaUtils';
import { omitFieldAndAriaProps } from './util/omitFieldProps';
import shiftInputCursor from './util/shiftInputCursor';

const StyledInputGroup = styled(BlueprintInputGroup)`
  ${space};

  /* have to force the font-family here, because Blueprint does not allow this to be overridden with SCSS */
  .${Classes.INPUT} {
    font-family: ${({ theme }) => theme.fonts.body};
  }

  // There is a blueprint CSS rule with high specificity (0,8,0) that overrides the button color within bp4-input-action, for some reason.  We may want to add other intents.
  .bp4-input-action {
    height: 100%; // BC w/o this there seems to be a 29px height so all alignment is just slightly off - but this COULD have wide impacts!
    .bp4-button.bp4-minimal:not(:hover):not(:focus) {
      &.bp4-button.bp4-intent-danger {
        color: ${({ theme }) => theme.colors.danger};
        background-color: transparent;
        border-color: transparent;

        &:hover {
          background-color: transparent;
        }
      }
    }
  }
  // A special case that should only apply to CmdLineMappingTable
  .cmd-line-value-buttons {
    padding-right: 1px;
  }

  &.${Classes.LARGE} {
    /* force large to be the normal Blueprint sized inputs, since by default we use small */
    input {
      height: 30px;
      font-size: 14px;
    }

    .bp4-input:not(:first-child) {
      padding-left: 35px;
    }

    > .bp4-icon,
    .bp4-input-action > .bp4-spinner {
      margin: 7px;
    }

    .${Classes.TAG} {
      min-width: 20px;
      min-height: 20px;
      padding: 0 8px;
      margin: 5px;
    }

    .bp4-button {
      min-width: 24px;
      min-height: 24px;
      margin: 3px;
    }
  }

  &.large {
    > .bp4-icon,
    .bp4-input-action > .bp4-spinner {
      margin: 10px;
    }

    .bp4-input-action .bp4-button {
      font-size: 14px;
    }
  }

  &.bp4-large {
    .bp4-input-action .bp4-button {
      font-size: 14px;
    }
  }

  &.jumbo {
    input {
      height: 40px;
      font-size: 16px;
      padding-left: 10px;
    }

    .bp4-input:not(:first-child) {
      padding-left: 40px;
    }

    .bp4-input-action {
      height: 100%;
    }

    > .bp4-icon,
    .bp4-input-action > .bp4-spinner {
      margin: 12px;
    }

    .bp4-button {
      min-width: 30px;
      min-height: 30px;
      margin: 0;
    }
  }

  &.round {
    input {
      border-radius: 50px;
    }
  }

  &.chips {
    .bp4-input-left-container {
      left: 5px;
      height: 100%;
      display: flex;
      align-items: center;
      padding-left: 10px;
      padding-right: 10px;
    }
  }

  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  // number input spinner arrows, be gone!
  input[type='number'] {
    -moz-appearance: textfield;
  }
`;

const InputGroup = (props) => {
  const {
    ariaLabel,
    autoComplete,
    autoFocus,
    autoInsertPrefix,
    buttonAriaLabel,
    clearable,
    display,
    field,
    height,
    helperTextId,
    labelId,
    maxWidth,
    onEnterKey,
    onQuery,
    rightElement,
    textAlign,
    width,

    ...rest
  } = props;

  let { onFocus } = props;
  if (autoFocus) {
    onFocus = (e) => {
      shiftInputCursor(e);

      if (props.onFocus) {
        props.onFocus(e);
      }
    };
  }

  const delayedInsertDebounced = useMemo(
    () =>
      debounce((e) => {
        const newValue = e.target.value;
        // assume autoComplete conflicts but there could be work arounds.  ..
        if (newValue === '' && autoInsertPrefix && field && autoComplete === 'off') {
          field.setValue(autoInsertPrefix);
        }
      }, 500),
    [autoInsertPrefix, field]
  );

  const onBlur = (e) => {
    if (field) {
      field.setPristine(false);
    }

    if (props.onBlur) {
      props.onBlur(e);
    } else {
      delayedInsertDebounced(e);
    }
  };

  // Handle onChange with autoInsertPrefix logic
  const handleChange = (e) => {
    const newValue = e.target.value || '';

    // Pass through to the original onChange
    if (rest.onChange) {
      rest.onChange(newValue);
    }
    // field.setValue does not work when the cursor is present.  Is it worth it to find another way to insert autoInsertPrefix?
    // else {
    //   delayedInsertDebounced(e); // doesnt work
    // }
  };

  // Handle clear button with autoInsertPrefix logic
  const handleClear = () => {
    if (autoInsertPrefix && field) {
      field.setValue(autoInsertPrefix);
    } else if (rest.onChange) {
      rest.onChange('');
    }
  };

  // Handle mouse wheel
  const handleWheel = (e) => {
    // prevent mouse wheel from simultaneously incrementing numeric inputs & scrolling the page
    if (props.type === 'number') {
      e.target.blur(); // remove focus and let the page scroll
    }
  };

  const dataAttributes = {};
  if (labelId) {
    dataAttributes['data-testid'] = `inputGroup-${labelId}`;
  }
  if (autoComplete === 'off') {
    dataAttributes['data-lpignore'] = 'true';
    dataAttributes.autoComplete = 'off';
  } else {
    dataAttributes.autoComplete = autoComplete;
  }

  const ariaProps = formAriaProps(
    props,
    { ariaRequired: rest.isRequired },
    {
      passOnExistingAria: true
    }
  );
  // Note inputRef is a prop in Blueprint and it could be coming from FieldState

  return (
    <StyledInputGroup
      {...ariaProps}
      {...omitFieldAndAriaProps(rest)}
      autoFocus={autoFocus}
      onFocus={onFocus}
      onBlur={onBlur}
      onWheel={handleWheel}
      onChange={autoInsertPrefix ? handleChange : rest.onChange} // Use custom handler only when autoInsertPrefix is set
      rightElement={
        rightElement ||
        (clearable && rest.value && (
          <Flex alignItems="center" height="100%">
            <Button minimal icon="cross" onClick={handleClear} ariaLabel={buttonAriaLabel} />
          </Flex>
        ))
      }
      style={{ display, maxWidth, width, height, textAlign }}
      {...dataAttributes}
    />
  );
};

InputGroup.defaultProps = {
  autoComplete: 'off'
};

export default InputGroup;
